'use client';

import { useCallback, useRef } from 'react';
import { Box, HStack } from '@terra/ui-styled-system';
import { Button } from '../../../../components/button';
import { Field } from '../../../../components/field';
import { IconButton } from '../../../../components/icon-button';
import { Input } from '../../../../components/input';
import { FieldWrapper } from '../../../../components/field-wrapper/FieldWrapper';
import { Delete02Icon, Upload01Icon } from '@terra/ui-icons';
import type { useTranslations } from 'next-intl';
import type {
  MtlsFormMeta,
  ValidationErrors,
} from '../AddCredentialsDialogStore';

export type MtlsCredentialFields = {
  mtlsCert: string;
  mtlsKey: string;
  mtlsPfx: string;
  mtlsOrigin: string;
  mtlsPassphrase: string;
};
const isPkcs12File = (fileName: string): boolean =>
  /\.(p12|pfx)$/i.test(fileName);
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
};

interface MtlsFileUploadProps {
  label: string;
  fileName: string;
  hasContent: boolean;
  accept: string;
  error?: string;
  disabled?: boolean;
  deleteAriaLabel: string;
  selectFileLabel: string;
  onFileSelect: (file: File) => void;
  onClear: () => void;
}
const MtlsFileUpload = ({
  label,
  fileName,
  hasContent,
  accept,
  error,
  disabled,
  deleteAriaLabel,
  selectFileLabel,
  onFileSelect,
  onClear,
}: MtlsFileUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Field.Root invalid={Boolean(error)}>
      <Field.Label fontSize="xs">
        <Box as="span" color="fg.neutral.error">
          *{' '}
        </Box>
        {label}
      </Field.Label>
      <Input
        ref={inputRef}
        type="file"
        display="none"
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];

          if (file) onFileSelect(file);
          e.target.value = '';
        }}
      />
      <Button
        variant="outline"
        size="sm"
        w="full"
        justifyContent="flex-start"
        fontWeight="normal"
        p="2"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        <Upload01Icon size={16} />
        {selectFileLabel}
      </Button>
      {hasContent && (
        <HStack
          gap={6}
          alignItems="center"
          px={2}
          py={1}
          borderWidth="1px"
          borderColor="border.neutral.subtle"
          borderRadius="md"
          bg="bg.neutral.light"
        >
          <Box fontSize="xs" fontWeight="medium" flex={1}>
            {fileName}
          </Box>
          <IconButton
            variant="ghost"
            size="xs"
            onClick={onClear}
            aria-label={deleteAriaLabel}
          >
            <Delete02Icon size={16} />
          </IconButton>
        </HStack>
      )}
      <Field.ErrorText fontSize="xs" color="fg.neutral.error">
        {error}
      </Field.ErrorText>
    </Field.Root>
  );
};

interface MtlsFieldsProps {
  mtlsCredential: MtlsCredentialFields;
  mtlsMeta: MtlsFormMeta;
  validationErrors: ValidationErrors;
  onCredentialChange: (values: Partial<MtlsCredentialFields>) => void;
  onMetaChange: (meta: MtlsFormMeta) => void;
  onValidationErrorsClear: (errors: Partial<ValidationErrors>) => void;
  t: ReturnType<typeof useTranslations>;
}

export const MtlsFields = ({
  mtlsCredential,
  mtlsMeta,
  validationErrors,
  onCredentialChange,
  onMetaChange,
  onValidationErrorsClear,
  t,
}: MtlsFieldsProps) => {
  const hasCertLikeContent =
    mtlsCredential.mtlsCert.length > 0 || mtlsCredential.mtlsPfx.length > 0;
  const hasPfx = mtlsCredential.mtlsPfx.length > 0;
  const hasKey = mtlsCredential.mtlsKey.length > 0;
  const handleCertFileSelect = useCallback(
    async (file: File) => {
      const isPfx = isPkcs12File(file.name);

      if (isPfx) {
        const content = arrayBufferToBase64(await file.arrayBuffer());

        onCredentialChange({
          mtlsCert: '',
          mtlsKey: '',
          mtlsPfx: content,
          mtlsPassphrase: '',
        });
        onMetaChange({
          ...mtlsMeta,
          certFileName: '',
          keyFileName: '',
          pfxFileName: file.name,
        });
      } else {
        const content = await file.text();

        onCredentialChange({
          mtlsCert: content,
          mtlsPfx: '',
          mtlsPassphrase: '',
        });
        onMetaChange({
          ...mtlsMeta,
          certFileName: file.name,
          pfxFileName: '',
        });
      }

      onValidationErrorsClear({ mtlsCertificate: '', mtlsKey: '' });
    },
    [mtlsMeta, onCredentialChange, onMetaChange, onValidationErrorsClear],
  );
  const handleKeyFileSelect = useCallback(
    async (file: File) => {
      const content = await file.text();

      onCredentialChange({
        mtlsKey: content,
        mtlsPfx: '',
        mtlsPassphrase: '',
      });
      onMetaChange({
        ...mtlsMeta,
        keyFileName: file.name,
        pfxFileName: '',
      });
      onValidationErrorsClear({ mtlsKey: '' });
    },
    [mtlsMeta, onCredentialChange, onMetaChange, onValidationErrorsClear],
  );
  const handleClearCert = useCallback(() => {
    onCredentialChange({
      mtlsCert: '',
      mtlsPfx: '',
      mtlsPassphrase: '',
    });
    onMetaChange({
      ...mtlsMeta,
      certFileName: '',
      pfxFileName: '',
    });
  }, [mtlsMeta, onCredentialChange, onMetaChange]);
  const handleClearKey = useCallback(() => {
    onCredentialChange({ mtlsKey: '' });
    onMetaChange({ ...mtlsMeta, keyFileName: '' });
  }, [mtlsMeta, onCredentialChange, onMetaChange]);

  return (
    <>
      <FieldWrapper
        label={t('mtls.origin.label')}
        error={validationErrors.mtlsOrigin}
        isRequired
      >
        <Input
          size="sm"
          placeholder={t('mtls.origin.placeholder')}
          value={mtlsCredential.mtlsOrigin}
          onChange={(e) => {
            onCredentialChange({ mtlsOrigin: e.target.value });
            onValidationErrorsClear({ mtlsOrigin: '' });
          }}
        />
      </FieldWrapper>
      <MtlsFileUpload
        label={t('mtls.certificate.label')}
        fileName={mtlsMeta.pfxFileName || mtlsMeta.certFileName}
        hasContent={hasCertLikeContent}
        accept=".pem,.crt,.cer,.p12,.pfx,.txt"
        error={validationErrors.mtlsCertificate}
        deleteAriaLabel={t('delete')}
        selectFileLabel={t('mtls.certificate.selectFile')}
        onFileSelect={(file) => void handleCertFileSelect(file)}
        onClear={handleClearCert}
      />

      {hasCertLikeContent && !hasPfx && (
        <MtlsFileUpload
          label={t('mtls.key.label')}
          fileName={mtlsMeta.keyFileName}
          hasContent={hasKey}
          accept=".pem,.key,.txt"
          error={validationErrors.mtlsKey}
          deleteAriaLabel={t('delete')}
          selectFileLabel={t('mtls.key.selectFile')}
          onFileSelect={(file) => void handleKeyFileSelect(file)}
          onClear={handleClearKey}
        />
      )}

      {hasPfx && (
        <Field.Root>
          <Field.Label fontSize="xs">{t('mtls.password.label')}</Field.Label>
          <Input
            size="sm"
            placeholder={t('mtls.password.placeholder')}
            type="password"
            value={mtlsCredential.mtlsPassphrase}
            onChange={(e) =>
              onCredentialChange({
                mtlsPassphrase: e.target.value,
              })
            }
          />
        </Field.Root>
      )}
    </>
  );
};
